import axios, { InternalAxiosRequestConfig } from 'axios';
import API_CONFIG from './api';
import { applyBlockedResponse } from '../services/platformBillingStore';
import {
  clearAuthStorageAndRedirectToLogin,
  shouldClearSessionOn401,
} from '../utils/authSessionRedirect';
import {
  isAssistantEndpoint,
  recordAssistantError,
  toAssistantErrorEndpoint,
} from '../utils/assistantErrorBuffer';

// Permite que chamadas específicas optem por NÃO redirecionar para login em 401
// (ex.: erros de provedores externos como Focus NFe), sem afetar a sessão.
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

// Configurar axios com URL base (sem /api/v1 pois já está nas chamadas)
import { getBackendBaseUrl } from './environment';

axios.defaults.baseURL = getBackendBaseUrl();
axios.defaults.timeout = API_CONFIG.TIMEOUT;
axios.defaults.headers.common['Content-Type'] = API_CONFIG.HEADERS['Content-Type'];

// Interceptor para requisições - autenticação + contexto de unidade (multi-unidade)
axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // FormData precisa sem Content-Type fixo: o browser define multipart + boundary.
    // O default application/json quebra upload (OFX, avatar, etc.) — multer recebe corpo vazio.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers;
      if (h && typeof (h as any).delete === 'function') {
        (h as any).delete('Content-Type');
      } else if (h) {
        delete (h as Record<string, unknown>)['Content-Type'];
      }
    }

    // Adicionar token de autenticação se disponível
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Anexar X-Unit-Id automaticamente quando houver unidade ativa.
    // O backend usa esse header como override do JWT em rotas protegidas
    // por requireUnitScope (apenas se o usuário pertencer à unidade).
    if (!config.headers['X-Unit-Id']) {
      try {
        const rawUser = localStorage.getItem('auth_user');
        if (rawUser) {
          const parsed = JSON.parse(rawUser);
          const unitId = parsed?.current_unit?.id;
          if (unitId) config.headers['X-Unit-Id'] = unitId;
        }
      } catch (_e) {
        // ignore JSON parse errors
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para respostas - apenas tratar erros de autenticação
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Só derruba sessão em 401 de JWT Klinvia (/auth/me ou mensagem conhecida).
    // `skipAuthRedirect` força não redirecionar (ex.: NF-e / Focus).
    if (shouldClearSessionOn401(error)) {
      clearAuthStorageAndRedirectToLogin();
    }

    // 402: acesso suspenso pela plataforma — mensalidade atrasada ou pagamento
    // de ativação pendente. Em vez de espalhar mensagens de erro por cada tela,
    // alimenta o store e deixa a árvore de rotas trocar o conteúdo pela tela de
    // pagamento correspondente.
    const blockCode = error.response?.data?.code;
    if (
      error.response?.status === 402 &&
      (blockCode === 'platform_billing_blocked' || blockCode === 'platform_activation_pending')
    ) {
      applyBlockedResponse(error.response.data);
    }

    return Promise.reject(error);
  }
);

// Guarda os últimos erros HTTP da tela para dar contexto à Central de IA.
// Nunca registra corpo, headers ou query string da requisição.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = Number(error?.response?.status || 0);
      if (status >= 400 && !shouldClearSessionOn401(error)) {
        const endpoint = toAssistantErrorEndpoint(error.config?.url);
        if (!isAssistantEndpoint(endpoint)) {
          const data = error.response?.data;
          const fromBody =
            data && typeof data === 'object'
              ? [data.error, data.message].find((v: unknown) => typeof v === 'string' && v.trim())
              : undefined;
          recordAssistantError({
            status,
            message: String(fromBody || error.message || ''),
            endpoint,
            method: error.config?.method,
            at: new Date().toISOString(),
          });
        }
      }
    } catch (_e) {
      // captura de contexto nunca pode alterar o erro original
    }
    return Promise.reject(error);
  }
);

export default axios; 